import { useEffect, useState } from "react";
import BioCard from "../components/BioCard";
import NextPage from "../components/NextPage";
import SEO from "../components/SEO";
import ChapterHero from "../components/ChapterHero";
import AdaptivePhoto from "../components/AdaptivePhoto";
import { Butterfly } from "../components/Butterflies";
import { SALTIE_BRAYS } from "../lib/data";
import { playSound } from "../lib/audio";
import { getSaltieBraysPage } from "../lib/api";
import useCharacterBios from "../hooks/useCharacterBios";
import { safeHtml } from "../lib/safeHtml";

const DEFAULT_PAGE = {
  seo_title: "Meet the Saltie Brays\u2122",
  seo_description: "The donkeys of the pasture \u2014 joyful, playful, full of gentle humor.",
  hero_title: "The Saltie Brays\u2122",
  hero_subtitle: "Joyful \u00b7 Playful \u00b7 Full of gentle humor.",
  intro_body: "The donkeys of the pasture each carry their own quiet wonder. Walk among them and see what they have to teach.",
  hear_a_bray_heading: "Hear a Bray. Pray.",
  hear_a_bray_subheading: "Every bray is a gentle reminder to pause and talk with God.",
  hear_a_bray_body_html:
    "<p>Out here in the pasture, a donkey\u2019s bray is more than a familiar sound. It has become a simple reminder to pause and pray.</p><p>The prayer does not have to be long or perfectly worded. It may be as simple as:</p><ul><li>\u201cThank You, Lord.\u201d</li><li>\u201cPlease guide me today.\u201d</li><li>\u201cWatch over my family.\u201d</li><li>\u201cHelp someone who needs You.\u201d</li><li>\u201cI\u2019m listening.\u201d</li></ul><p>One little bray can become one little prayer. And those small moments have a beautiful way of turning our hearts back toward God.</p>",
  hear_a_bray_closing: "Hear a Bray. Pray.",
  hear_a_bray_photo_url: "",
  hear_a_bray_photo_alt: "Bonnie the donkey in the pasture",
  next_button_label: "Meet the Saltie Moos\u2122",
};

export default function SaltieBrays() {
  const brays = useCharacterBios(SALTIE_BRAYS);
  const [page, setPage] = useState(DEFAULT_PAGE);
  useEffect(() => {
    playSound("/audio/saltie-bray.mp3", 0.9);
    getSaltieBraysPage()
      .then((p) => setPage({ ...DEFAULT_PAGE, ...(p || {}) }))
      .catch(() => {});
  }, []);
  return (
    <div className="parchment min-h-screen" data-testid="saltie-brays-page">
      <SEO title={page.seo_title} description={page.seo_description} path="/saltie-brays" />
      <ChapterHero
        path="/saltie-brays"
        image="/assets/heroes/saltie-brays.webp"
        title={page.hero_title}
        subtitle={page.hero_subtitle}
        position="top-left"
      />
      <div className="max-w-6xl mx-auto px-6 -mt-8 relative z-10 pb-12">
        <p className="font-body text-lg text-[#2C2724]/85 max-w-2xl mx-auto text-center leading-relaxed mb-14">
          {page.intro_body}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {brays.map((b, i) => {
            const tints = ["#2DE181", "#579DE6", "#D62839", "#C8A876"];
            return (
              <BioCard
                key={b.slug}
                slug={b.slug}
                name={b.name}
                role={b.role}
                intro={b.intro}
                photoLabel={b.name}
                accent={tints[i % tints.length]}
              />
            );
          })}
        </div>
      </div>

      {/* ── Hear a Bray. Pray. ─────────────────────────────────── */}
      <section
        data-testid="hear-a-bray-section"
        className="relative max-w-6xl mx-auto px-6 pb-24 pt-6"
      >
        <div className="grid md:grid-cols-2 gap-10 md:gap-14 items-center">
          {/* Photo LEFT on desktop; stacks first (above) on mobile */}
          <div className="order-1 md:order-1">
            <AdaptivePhoto
              testId="hear-a-bray-photo"
              src={page.hear_a_bray_photo_url}
              alt={page.hear_a_bray_photo_alt}
              frameColor="#C8A876"
              tilt="-0.5deg"
              placeholder="Bonnie photo coming soon"
            />
          </div>

          {/* Text RIGHT on desktop; stacks below photo on mobile */}
          <div className="order-2 md:order-2 text-center md:text-left">
            <div className="flex md:justify-start justify-center mb-3 opacity-90">
              <Butterfly />
            </div>
            <h2 className="font-display text-3xl md:text-4xl text-[#4B3A2F] leading-tight">
              {page.hear_a_bray_heading}
            </h2>
            {page.hear_a_bray_subheading && (
              <p className="mt-3 font-script text-2xl md:text-3xl text-[#A87149]">
                {page.hear_a_bray_subheading}
              </p>
            )}
            <div className="gold-rule my-6 mx-auto md:mx-0 w-24" />
            <div
              className="font-body text-lg text-[#2C2724]/90 leading-relaxed prose-winks text-left"
              dangerouslySetInnerHTML={safeHtml(page.hear_a_bray_body_html)}
            />
            {page.hear_a_bray_closing && (
              <p className="mt-8 font-display italic text-2xl md:text-3xl text-[#4B3A2F]">
                {page.hear_a_bray_closing}
              </p>
            )}
          </div>
        </div>
      </section>

      <NextPage to="/saltie-moos" label={page.next_button_label} />
    </div>
  );
}
